import type { Channel } from '../types';

// What a visitor can see once a channel is searchable/public. Derived from
// what the public routes actually serve: GET /public/channels/{nickname}
// (profile + view settings), GET /signals/{id} (readable for a public
// channel), and /public/channels/{nickname}/report(s). Kept as plain data
// so the confirm sheet and its test share one source of truth.
export interface DisclosureItem {
  label: string;
  detail: string;
}

export interface Disclosure {
  visible: DisclosureItem[];
  hidden: DisclosureItem[];
  warnings: string[];
  url: string;
}

export function buildDisclosure(channel: Channel, origin: string): Disclosure {
  const visible: DisclosureItem[] = [
    { label: 'Name and description', detail: `"${channel.title}"${channel.description ? ` — ${channel.description}` : ' (no description yet)'}` },
    {
      label: 'Tags',
      detail: channel.tags.length ? channel.tags.map((t) => `#${t}`).join(' ') : 'None',
    },
    { label: 'Avatar and colour', detail: 'The channel avatar and accent colour.' },
    {
      label: 'Signals and messages',
      detail: 'Everything posted in this channel, including past history, becomes readable by anyone with the link.',
    },
    { label: 'Charts', detail: 'The channel chart with its configured filters.' },
    { label: 'AI reports', detail: 'The latest report and the full report history.' },
    { label: 'Discovery', detail: 'The channel appears in public search and the Explore list.' },
  ];
  const hidden: DisclosureItem[] = [
    { label: 'Members and invites', detail: 'Never shown.' },
    { label: 'AI keys, ingest tokens, webhook secrets', detail: 'Never shown, and never copied by "Create similar".' },
    { label: 'Workflows', detail: 'Never shown.' },
  ];
  const warnings: string[] = [];
  if (!channel.description.trim()) warnings.push('This channel has no description, so visitors will not know what it is for.');
  if (channel.tags.length === 0) warnings.push('No tags: the channel is harder to find by topic.');
  return { visible, hidden, warnings, url: `${origin}/public/${channel.nickname}` };
}
